import { useEffect, useState } from "react";
import { ApprovalStatus, MeetingLimitedResponse } from "client/openapi";
import { DialogContent } from "components/Dialog";
import { Button, ButtonColor } from "components/Button";

interface OverrideTutorApprovalsDialogContentProps {
  meeting: MeetingLimitedResponse;
  onSave: (overrides: Record<number, ApprovalStatus>) => void;
  onCancel: () => void;
}

export default function OverrideTutorApprovalsDialogContent({
  meeting,
  onSave,
  onCancel,
}: OverrideTutorApprovalsDialogContentProps) {
  const [tutorOverrides, setTutorOverrides] = useState<
    Record<number, ApprovalStatus>
  >({});

  // Initialize overrides with current tutor approval statuses
  useEffect(() => {
    const initialOverrides: Record<number, ApprovalStatus> = {};
    meeting.tutors.forEach((tutor) => {
      initialOverrides[tutor.id] = tutor.reviewed_and_approved_by_tutor;
    });
    setTutorOverrides(initialOverrides);
  }, [meeting]);

  const handleTutorApprovalChange = (
    tutorId: number,
    status: ApprovalStatus
  ) => {
    setTutorOverrides((prev) => ({
      ...prev,
      [tutorId]: status,
    }));
  };

  const handleSave = () => {
    onSave(tutorOverrides);
  };

  return (
    <DialogContent className="max-w-3xl dialog-content dialog-content--left wide bg-neutral-50 font-montserrat">
      <div className="flex flex-col h-full">
        {/* Header */}
        <div className="px-4 py-3 border-b border-gray-200 bg-white">
          <h2 className="text-xl font-bold text-gray-900">
            Override Tutor Approvals
          </h2>
          <p className="text-xs text-gray-600 mt-1">
            {meeting.locked
              ? "Meeting is locked - viewing only"
              : "Override individual tutor approval statuses for this meeting"}
          </p>
        </div>

        {/* Content */}
        <div className="flex-1 px-4 py-4 overflow-y-auto">
          <div className="space-y-3">
            {meeting.tutors.map((tutor) => (
              <div
                key={tutor.id}
                className={`bg-white rounded-lg border border-gray-200 shadow-sm ${
                  meeting.locked ? "opacity-60" : ""
                }`}
              >
                <div className="p-4">
                  {/* Tutor Info and Approval Options in one row */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                        <span className="text-blue-600 font-semibold text-sm">
                          {tutor.tutor.first_name[0]}
                          {tutor.tutor.last_name[0]}
                        </span>
                      </div>
                      <div>
                        <h3 className="text-base font-semibold text-gray-900">
                          {tutor.tutor.first_name} {tutor.tutor.last_name}
                        </h3>
                        <p className="text-xs text-gray-500">
                          Current:
                          <span
                            className={`ml-1 font-medium ${
                              tutor.reviewed_and_approved_by_tutor ===
                              ApprovalStatus.APPROVED
                                ? "text-green-600"
                                : tutor.reviewed_and_approved_by_tutor ===
                                  ApprovalStatus.DENIED
                                ? "text-red-600"
                                : "text-yellow-600"
                            }`}
                          >
                            {tutor.reviewed_and_approved_by_tutor}
                          </span>
                        </p>
                      </div>
                    </div>

                    {/* Approval Options */}
                    <div className="flex space-x-4">
                      <label
                        className={`flex items-center space-x-2 ${
                          meeting.locked
                            ? "cursor-not-allowed"
                            : "cursor-pointer"
                        }`}
                      >
                        <input
                          type="radio"
                          name={`tutor-${tutor.id}`}
                          checked={
                            tutorOverrides[tutor.id] === ApprovalStatus.APPROVED
                          }
                          onChange={() =>
                            !meeting.locked &&
                            handleTutorApprovalChange(
                              tutor.id,
                              ApprovalStatus.APPROVED
                            )
                          }
                          disabled={meeting.locked}
                          className="w-4 h-4 text-green-600 border-gray-300 focus:ring-green-500 disabled:opacity-50"
                        />
                        <span
                          className={`text-sm ${
                            meeting.locked ? "text-gray-400" : "text-gray-700"
                          }`}
                        >
                          Approve
                        </span>
                      </label>

                      <label
                        className={`flex items-center space-x-2 ${
                          meeting.locked
                            ? "cursor-not-allowed"
                            : "cursor-pointer"
                        }`}
                      >
                        <input
                          type="radio"
                          name={`tutor-${tutor.id}`}
                          checked={
                            tutorOverrides[tutor.id] === ApprovalStatus.DENIED
                          }
                          onChange={() =>
                            !meeting.locked &&
                            handleTutorApprovalChange(
                              tutor.id,
                              ApprovalStatus.DENIED
                            )
                          }
                          disabled={meeting.locked}
                          className="w-4 h-4 text-red-600 border-gray-300 focus:ring-red-500 disabled:opacity-50"
                        />
                        <span
                          className={`text-sm ${
                            meeting.locked ? "text-gray-400" : "text-gray-700"
                          }`}
                        >
                          Deny
                        </span>
                      </label>

                      <label
                        className={`flex items-center space-x-2 ${
                          meeting.locked
                            ? "cursor-not-allowed"
                            : "cursor-pointer"
                        }`}
                      >
                        <input
                          type="radio"
                          name={`tutor-${tutor.id}`}
                          checked={
                            tutorOverrides[tutor.id] === ApprovalStatus.PENDING
                          }
                          onChange={() =>
                            !meeting.locked &&
                            handleTutorApprovalChange(
                              tutor.id,
                              ApprovalStatus.PENDING
                            )
                          }
                          disabled={meeting.locked}
                          className="w-4 h-4 text-yellow-600 border-gray-300 focus:ring-yellow-500 disabled:opacity-50"
                        />
                        <span
                          className={`text-sm ${
                            meeting.locked ? "text-gray-400" : "text-gray-700"
                          }`}
                        >
                          Pending
                        </span>
                      </label>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Footer with Save Button */}
        <div className="px-4 py-3 border-t border-gray-200 bg-gray-50">
          <div className="flex justify-center">
            <Button
              color={ButtonColor.GREEN}
              onClick={handleSave}
              disabled={meeting.locked}
            >
              {meeting.locked ? "Meeting Locked" : "Save Changes"}
            </Button>
          </div>
        </div>
      </div>
    </DialogContent>
  );
}
